import {
  Button,
  Input,
  InputType,
  Typography,
  Checkbox,
} from '../../components/primitives';
import {
  TermsAndConditions,
  TermsAndConditionsEnum,
} from '../../components/text/TermsAndConditions';
import Alert from '../../components/ui/Alert';
import { PasswordStrengthIndicator } from '../../components/ui/PasswordStrengthIndicator';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { Environment } from '../../env/Environment';
import { Icons } from '../../icons';
import {
  SearchParamsBuilder,
  testId,
  toParams,
  getRecaptchaLanguage,
} from '../../utils';
import { PASSWORD_MIN, PASSWORD_MAX } from '../../utils/constants';
import { PageCard } from './PageCard';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cornerstone } from '@rpi/api';
import { useTranslation, Trans } from '@rpi/i18n';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

export const SignUpRoute: React.FC = () => {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'route.auth' });
  const [searchParams] = useSearchParams();
  const SignUpSchema = z
    .object({
      email: z.string().email(`${t('email_requirements')}`),
      password: z
        .string()
        .min(PASSWORD_MIN, `${t('minPassword', { minChar: PASSWORD_MIN })}`),
      wantsNewsletter: z.boolean(),
      recaptchaToken: z.string({
        required_error: `${t('recaptcha_required')}`,
        invalid_type_error: `${t('recaptcha_required')}`,
      }),
    })
    .required();

  type SignUpSchema = z.infer<typeof SignUpSchema>;

  const form = useForm<SignUpSchema & { recaptchaToken: string | null }>({
    resolver: zodResolver(SignUpSchema),
    defaultValues: {
      email: searchParams.get('email') ?? '',
      password: '',
      wantsNewsletter: false,
      recaptchaToken: undefined,
    },
    mode: 'onBlur',
  });

  const navigate = useNavigate();

  const { isError, isLoading, error, mutate } = Cornerstone.useSignUp({
    mutation: {
      onSuccess(request) {
        const data = request.data;

        if (data.requires2StepVerification) {
          return navigate(
            toParams(
              '/auth/sign-up/confirm',
              new SearchParamsBuilder(searchParams)
                .filter((k) => k === 'redirect')
                .add('email', form.getValues().email),
            ),
          );
        }

        navigate(
          toParams(
            '/auth/session',
            new SearchParamsBuilder(searchParams).filter(
              (k) => k === 'redirect',
            ),
          ),
        );
      },
      onError() {},
    },
  });

  function onSubmit(data: z.infer<typeof SignUpSchema>) {
    if (!form.getValues().recaptchaToken) {
      form.setError('recaptchaToken', {
        type: 'manual',
        message: t('recaptcha_required') as string,
      });
      return;
    }
    mutate({
      data: {
        email: data.email,
        password: data.password,
        redirectUrl: searchParams.get('redirect') ?? undefined,
        settings: { wantsNewsletter: data.wantsNewsletter },
      },
    });
  }

  const alertText = useMemo(() => {
    const mutationData = error?.response?.data as any;
    if (mutationData?.errorCode === Cornerstone.ErrorCode.USER_ALREADY_EXISTS) {
      return t('user_already_registered');
    }
    if (mutationData?.errorCode === Cornerstone.ErrorCode.EMAIL_DOMAIN_BANNED) {
      return (
        <Trans
          i18nKey={t('email_domain_banned')}
          components={{
            'support-link': (
              <a
                aria-label="support"
                href="https://support.blurb.com/hc/en-us"
                className="underline hover:no-underline text-red-dark"
              />
            ),
          }}
        />
      );
    }
    if (mutationData?.errorCode === Cornerstone.ErrorCode.PASSWORD_TOO_SHORT) {
      return t('minPassword', { minChar: PASSWORD_MIN });
    }
    if (isError) return t('generic_error');
    return null;
  }, [isError, error, t]);

  return (
    <>
      <PageCard>
        <Form {...form}>
          <form
            noValidate
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            {alertText && (
              <Alert data-testid="signup-alert" variant="error">
                {alertText}
              </Alert>
            )}
            <Typography variant="title-default" as="h1" className="text-center">
              {t('signup_title')}
            </Typography>
            <RequiredFieldIndicator />
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel {...testId('email', 'label')}>
                    <Typography variant="form-label">
                      <Trans
                        i18nKey="route.auth.email"
                        components={{
                          'required-indicator': <Icons.Indicator />,
                        }}
                      />
                    </Typography>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...testId('email')}
                      type={InputType.EMAIL}
                      placeholder=""
                      {...field}
                      invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormMessage {...testId('email', 'message')} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel {...testId('password', 'label')}>
                    <Typography variant="form-label">
                      <Trans
                        i18nKey="route.auth.password"
                        components={{
                          'required-indicator': <Icons.Indicator />,
                        }}
                      />
                    </Typography>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...testId('password')}
                      type={InputType.PASSWORD}
                      placeholder=" "
                      maxLength={PASSWORD_MAX}
                      {...field}
                      invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormMessage {...testId('password', 'message')} />
                  <PasswordStrengthIndicator
                    password={form.watch('password')}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="recaptchaToken"
              render={({ field, fieldState }) => {
                const message = fieldState.error?.message;
                return (
                  // Need !important because FormItem assumes flex-col
                  <div className="flex justify-center">
                    <FormItem
                      className={clsx(
                        'flex gap-1 ',
                        message ? 'border border-red p-1' : '',
                      )}
                    >
                      <FormControl>
                        <ReCAPTCHA
                          sitekey={Environment.RECAPTCHA_SITE_KEY}
                          onChange={field.onChange}
                          hl={getRecaptchaLanguage(i18n.language)}
                        />
                      </FormControl>
                      {message && (
                        <Typography
                          variant="footnote"
                          className="text-red font-medium"
                          {...testId('recaptcha', 'message')}
                        >
                          {message}
                        </Typography>
                      )}
                    </FormItem>
                  </div>
                );
              }}
            />

            <FormField
              control={form.control}
              name="wantsNewsletter"
              render={({ field }) => (
                // Need !important because FormItem assumes flex-col
                <FormItem className="flex !flex-row gap-1">
                  <FormControl>
                    <Checkbox
                      {...testId('newsletter')}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel {...testId('newsletter', 'label')}>
                    <Typography variant="body">
                      {t('signup_wants_newsletter')}
                    </Typography>
                  </FormLabel>
                </FormItem>
              )}
            />
            <TermsAndConditions termsEnum={TermsAndConditionsEnum.SIGNUP} />

            <Button
              {...testId('submit')}
              type="submit"
              className="w-full"
              loading={isLoading}
            >
              {t('create_account')}
            </Button>
            <Typography variant="body" className="text-center">
              <Trans
                i18nKey="route.auth.signup_have_account"
                components={{
                  'signin-link': (
                    <Link
                      to={toParams(
                        '/auth/sign-in',
                        new SearchParamsBuilder(searchParams).add(
                          'email',
                          form.getValues().email,
                        ),
                      )}
                    />
                  ),
                }}
              />
            </Typography>
          </form>
        </Form>
      </PageCard>
    </>
  );
};
